<template>
  <main class="buyer-home">

    <!-- =========================
         HERO SECTION
    ========================== -->

    <section class="buyer-hero">

      <div class="hero-overlay"></div>

      <div class="container hero-container">

        <div class="buyer-content">

          <span class="eyebrow">
            WELCOME, {{ userName.toUpperCase() }}
          </span>

          <h1>
            Find your next
            <span>great read.</span>
          </h1>

          <p class="hero-description">
            Explore stories, discover new worlds,
            and find the books you'll never want
            to put down.
          </p>

          <div class="hero-buttons">

            <router-link
              to="/books"
              class="hero-btn primary-btn"
            >
              Explore Books
              <span>→</span>
            </router-link>

            <router-link
              to="/wishlist"
              class="hero-btn secondary-btn"
            >
              ♡ My Wishlist
            </router-link>

          </div>


          <!-- Statistics -->

          <div class="hero-stats">

            <div class="hero-stat">

              <strong>
                {{ books.length }}+
              </strong>

              <span>
                Books
              </span>

            </div>

            <div class="stat-divider"></div>

            <div class="hero-stat">

              <strong>
                10
              </strong>

              <span>
                Categories
              </span>

            </div>

            <div class="stat-divider"></div>

            <div class="hero-stat">

              <strong>
                ∞
              </strong>

              <span>
                Stories
              </span>

            </div>

          </div>

        </div>


        <!-- Decorative Books -->

        <div class="hero-decoration">

          <div class="floating-book book-one">
            <div class="book-spine"></div>
          </div>

          <div class="floating-book book-two">
            <div class="book-spine"></div>
          </div>

          <div class="floating-book book-three">
            <div class="book-spine"></div>
          </div>

          <div class="floating-circle circle-one"></div>

          <div class="floating-circle circle-two"></div>

        </div>

      </div>

    </section>


    <!-- =========================
         WELCOME STRIP
    ========================== -->

    <section class="welcome-section">

      <div class="container">

        <div class="welcome-card">

          <div class="welcome-icon">
            ✦
          </div>

          <div>

            <span class="welcome-label">
              YOUR NEXT CHAPTER STARTS HERE
            </span>

            <h2>
              Stories worth getting lost in.
            </h2>

            <p>
              From timeless classics to modern
              favorites, there's always something
              new waiting for you.
            </p>

          </div>

        </div>

      </div>

    </section>


    <!-- =========================
         CATEGORIES
    ========================== -->

    <section class="categories-section">

      <div class="container">

        <div class="section-heading">

          <div>

            <span class="section-label">
              EXPLORE
            </span>

            <h2>
              Find your kind of story
            </h2>

            <p>
              Browse books by the mood you're in.
            </p>

          </div>

          <router-link
            to="/books"
            class="section-link"
          >
            Browse All
            <span>→</span>
          </router-link>

        </div>


        <div class="category-grid">

          <!-- FICTION -->

          <router-link
            :to="{
              path: '/books',
              query: {
                category: 'Fiction'
              }
            }"
            class="category-card category-fiction"
          >

            <div class="category-icon">
              📖
            </div>

            <div class="category-text">

              <h3>
                Fiction
              </h3>

              <p>
                Escape into another world.
              </p>

            </div>

            <span class="category-arrow">
              →
            </span>

          </router-link>


          <!-- ROMANCE -->

          <router-link
            :to="{
              path: '/books',
              query: {
                category: 'Romance'
              }
            }"
            class="category-card category-romance"
          >

            <div class="category-icon">
              ♡
            </div>

            <div class="category-text">

              <h3>
                Romance
              </h3>

              <p>
                Stories about love and life.
              </p>

            </div>

            <span class="category-arrow">
              →
            </span>

          </router-link>


          <!-- FANTASY -->

          <router-link
            :to="{
              path: '/books',
              query: {
                category: 'Fantasy'
              }
            }"
            class="category-card category-fantasy"
          >

            <div class="category-icon">
              ✨
            </div>

            <div class="category-text">

              <h3>
                Fantasy
              </h3>

              <p>
                Enter worlds beyond imagination.
              </p>

            </div>

            <span class="category-arrow">
              →
            </span>

          </router-link>


          <!-- THRILLER -->

          <router-link
            :to="{
              path: '/books',
              query: {
                category: 'Thriller'
              }
            }"
            class="category-card category-thriller"
          >

            <div class="category-icon">
              ◈
            </div>

            <div class="category-text">

              <h3>
                Thriller
              </h3>

              <p>
                Turn the page. Feel the tension.
              </p>

            </div>

            <span class="category-arrow">
              →
            </span>

          </router-link>

        </div>

      </div>

    </section>


    <!-- =========================
         FEATURED BOOKS
    ========================== -->

    <section class="featured-section">

      <div class="container">

        <div class="section-heading">

          <div>

            <span class="section-label">
              HANDPICKED FOR YOU
            </span>

            <h2>
              Featured Books
            </h2>

            <p>
              Discover something you'll love.
            </p>

          </div>

          <router-link
            to="/books"
            class="section-link"
          >
            View All
            <span>→</span>
          </router-link>

        </div>


        <!-- Loading -->

        <div
          v-if="loading"
          class="loading-container"
        >

          <div
            class="spinner-border"
          ></div>

          <p>
            Discovering your next read...
          </p>

        </div>


        <!-- Books -->

        <div
          v-else
          class="featured-grid"
        >

          <BookCard
            v-for="book in featuredBooks"
            :key="book.id || book.ISBN"
            :book="book"
            :wishlist="wishlist"
            :can-manage="false"
            @add-to-wishlist="
              addToWishlist
            "
          />

        </div>

      </div>

    </section>


    <!-- =========================
         CTA
    ========================== -->

    <section class="cta-section">

      <div class="container">

        <div class="cta-card">

          <div class="cta-content">

            <span class="section-label">
              KEEP EXPLORING
            </span>

            <h2>
              Your next favorite book
              is waiting.
            </h2>

            <p>
              Take a look through our collection
              and discover a story made for you.
            </p>

            <router-link
              to="/books"
              class="cta-button"
            >
              Explore the Collection
              <span>→</span>
            </router-link>

          </div>


          <div class="cta-decoration">

            <div class="cta-circle"></div>

            <div class="cta-book">
              📚
            </div>

          </div>

        </div>

      </div>

    </section>

  </main>
</template>


<script>
import BookCard
  from '../components/BookCard/BookCard.vue'

export default {
  name: 'BuyerHome',

  components: {
    BookCard
  },

  computed: {

    books() {
      return this.$store.state.books
    },

    wishlist() {
      return this.$store.state.wishlist
    },

    loading() {
      return this.$store.state.loading
    },

    userName() {

      const user =
        this.$store.getters.currentUser

      return user
        ? user.name
        : 'Buyer'
    },

    featuredBooks() {
      return this.books.slice(0, 6)
    }

  },

  methods: {

    addToWishlist(book) {

      this.$store.dispatch(
        'addToWishlist',
        book
      )

    }

  }
}
</script>


<style scoped>

/* ========================================
   PAGE
======================================== */

.buyer-home {
  background:
    var(--background);

  color:
    var(--text);

  overflow:
    hidden;
}


/* ========================================
   HERO
======================================== */

.buyer-hero {
  position:
    relative;

  min-height:
    620px;

  display:
    flex;

  align-items:
    center;

  overflow:
    hidden;

  background:
    linear-gradient(
      120deg,
      #24113f 0%,
      #4b2078 45%,
      #8e4db2 100%
    );
}


.hero-overlay {
  position:
    absolute;

  inset:
    0;

  background:
    linear-gradient(
      90deg,
      rgba(25, 10, 45, 0.96),
      rgba(55, 21, 85, 0.78),
      rgba(123, 63, 153, 0.28)
    );

  z-index:
    1;
}


.hero-container {
  position:
    relative;

  z-index:
    2;

  min-height:
    620px;

  display:
    flex;

  align-items:
    center;
}


.buyer-content {
  position:
    relative;

  max-width:
    720px;

  padding:
    70px 0;

  z-index:
    5;
}


.eyebrow {
  display:
    inline-block;

  color:
    #ddd0ff;

  font-size:
    0.75rem;

  letter-spacing:
    3px;

  font-weight:
    800;
}


.buyer-content h1 {
  margin:
    10px 0 20px;

  font-size:
    clamp(
      3.2rem,
      6vw,
      5.8rem
    );

  line-height:
    0.98;

  letter-spacing:
    -2px;

  font-weight:
    800;

  color:
    #ffffff;
}


.buyer-content h1 span {
  display:
    block;

  background:
    linear-gradient(
      90deg,
      #ffffff,
      #d8bfff
    );

  -webkit-background-clip:
    text;

  -webkit-text-fill-color:
    transparent;
}


.hero-description {
  max-width:
    610px;

  color:
    rgba(255,255,255,0.82);

  font-size:
    1.08rem;

  line-height:
    1.8;

  margin-bottom:
    28px;
}


/* ========================================
   HERO BUTTONS
======================================== */

.hero-buttons {
  display:
    flex;

  gap:
    12px;

  flex-wrap:
    wrap;
}


.hero-btn {
  display:
    inline-flex;

  align-items:
    center;

  justify-content:
    center;

  gap:
    10px;

  min-height:
    52px;

  padding:
    0 24px;

  border-radius:
    12px;

  text-decoration:
    none;

  font-weight:
    700;

  transition:
    all 0.25s ease;
}


.primary-btn {
  background:
    #ffffff;

  color:
    #4b2078;

  box-shadow:
    0 10px 30px
    rgba(0,0,0,0.18);
}


.primary-btn:hover {
  color:
    #3b165f;

  transform:
    translateY(-3px);
}


.secondary-btn {
  border:
    1px solid
    rgba(255,255,255,0.55);

  color:
    #ffffff;

  background:
    rgba(255,255,255,0.08);
}


.secondary-btn:hover {
  color:
    #ffffff;

  background:
    rgba(255,255,255,0.17);

  transform:
    translateY(-3px);
}


/* ========================================
   HERO STATS
======================================== */

.hero-stats {
  display:
    flex;

  align-items:
    center;

  gap:
    22px;

  margin-top:
    42px;
}


.hero-stat {
  display:
    flex;

  flex-direction:
    column;
}


.hero-stat strong {
  color:
    #ffffff;

  font-size:
    1.35rem;

  font-weight:
    800;
}


.hero-stat span {
  color:
    rgba(255,255,255,0.6);

  font-size:
    0.75rem;
}


.stat-divider {
  width:
    1px;

  height:
    32px;

  background:
    rgba(255,255,255,0.25);
}


/* ========================================
   HERO DECORATION
======================================== */

.hero-decoration {
  position:
    absolute;

  right:
    -30px;

  top:
    0;

  width:
    50%;

  height:
    100%;
}


.floating-book {
  position:
    absolute;

  width:
    170px;

  height:
    250px;

  border-radius:
    6px 12px 12px 6px;

  box-shadow:
    20px 25px 50px
    rgba(0,0,0,0.35);
}


.book-one {
  right:
    23%;

  top:
    24%;

  transform:
    rotate(13deg);

  background:
    linear-gradient(
      135deg,
      #c8a8ff,
      #6940a4
    );
}


.book-two {
  right:
    40%;

  top:
    37%;

  transform:
    rotate(-9deg);

  background:
    linear-gradient(
      135deg,
      #f3c5d9,
      #a05278
    );
}


.book-three {
  right:
    8%;

  top:
    32%;

  transform:
    rotate(21deg);

  background:
    linear-gradient(
      135deg,
      #ddd2f4,
      #71549b
    );
}


.book-spine {
  position:
    absolute;

  left:
    12px;

  top:
    0;

  bottom:
    0;

  width:
    4px;

  background:
    rgba(255,255,255,0.3);
}


.floating-circle {
  position:
    absolute;

  border-radius:
    50%;

  border:
    1px solid
    rgba(255,255,255,0.18);
}


.circle-one {
  width:
    320px;

  height:
    320px;

  right:
    -80px;

  top:
    60px;
}


.circle-two {
  width:
    170px;

  height:
    170px;

  right:
    34%;

  bottom:
    70px;
}


/* ========================================
   WELCOME
======================================== */

.welcome-section {
  padding:
    55px 0 20px;
}


.welcome-card {
  display:
    flex;

  align-items:
    center;

  gap:
    22px;

  padding:
    25px 30px;

  border-radius:
    18px;

  background:
    var(--surface);

  border:
    1px solid
    var(--border);

  box-shadow:
    0 12px 35px
    rgba(78,42,110,0.07);
}


.welcome-icon {
  width:
    52px;

  height:
    52px;

  flex-shrink:
    0;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border-radius:
    14px;

  background:
    linear-gradient(
      135deg,
      #7c3aed,
      #c084fc
    );

  color:
    white;

  font-size:
    1.5rem;
}


.welcome-label,
.section-label {
  display:
    block;

  color:
    var(--primary);

  font-size:
    0.7rem;

  font-weight:
    800;

  letter-spacing:
    2px;
}


.welcome-card h2 {
  color:
    var(--text);

  font-size:
    1.5rem;

  margin:
    4px 0;

  font-weight:
    750;
}


.welcome-card p {
  color:
    var(--text-secondary);

  margin:
    0;

  font-size:
    0.92rem;
}


/* ========================================
   SECTIONS
======================================== */

.categories-section,
.featured-section {
  padding:
    65px 0;
}


.section-heading {
  display:
    flex;

  justify-content:
    space-between;

  align-items:
    flex-end;

  gap:
    20px;

  margin-bottom:
    30px;
}


.section-heading h2 {
  color:
    var(--text);

  font-size:
    2rem;

  font-weight:
    800;

  margin:
    4px 0 5px;
}


.section-heading p {
  color:
    var(--text-secondary);

  margin:
    0;
}


.section-link {
  display:
    inline-flex;

  align-items:
    center;

  gap:
    7px;

  color:
    var(--primary);

  text-decoration:
    none;

  font-weight:
    700;

  padding:
    9px 14px;

  border:
    1px solid
    var(--primary);

  border-radius:
    9px;

  transition:
    all 0.2s ease;
}


.section-link:hover {
  color:
    white;

  background:
    var(--primary);
}


/* ========================================
   CATEGORY CARDS
======================================== */

.category-grid {
  display:
    grid;

  grid-template-columns:
    repeat(4, 1fr);

  gap:
    16px;
}


.category-card {
  position:
    relative;

  min-height:
    170px;

  padding:
    24px;

  display:
    flex;

  flex-direction:
    column;

  justify-content:
    space-between;

  border-radius:
    18px;

  text-decoration:
    none;

  overflow:
    hidden;

  border:
    1px solid
    var(--border);

  background:
    var(--surface);

  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
}


.category-card:hover {
  transform:
    translateY(-6px);

  box-shadow:
    0 15px 35px
    rgba(80,45,110,0.12);
}


.category-fiction {
  background:
    linear-gradient(
      135deg,
      #f2ecff,
      #ffffff
    );
}


.category-romance {
  background:
    linear-gradient(
      135deg,
      #fff0f7,
      #ffffff
    );
}


.category-fantasy {
  background:
    linear-gradient(
      135deg,
      #f0f3ff,
      #ffffff
    );
}


.category-thriller {
  background:
    linear-gradient(
      135deg,
      #f1efff,
      #ffffff
    );
}


.category-icon {
  width:
    45px;

  height:
    45px;

  display:
    flex;

  align-items:
    center;

  justify-content:
    center;

  border-radius:
    13px;

  background:
    rgba(124,58,237,0.1);

  color:
    var(--primary);

  font-size:
    1.25rem;

  margin-bottom:
    20px;
}


.category-card h3 {
  color:
    #32184f;

  font-size:
    1.1rem;

  font-weight:
    800;

  margin:
    0 0 5px;
}


.category-card p {
  color:
    #766c80;

  font-size:
    0.82rem;

  margin:
    0;
}


.category-arrow {
  position:
    absolute;

  right:
    20px;

  top:
    20px;

  color:
    var(--primary);

  font-size:
    1.2rem;

  font-weight:
    700;
}


.category-card,
.category-card:hover,
.category-card:focus,
.category-card:visited {
  text-decoration:
    none;
}


/* ========================================
   FEATURED
======================================== */

.featured-section {
  background:
    linear-gradient(
      180deg,
      var(--background),
      var(--surface)
    );
}


.featured-grid {
  display:
    grid;

  grid-template-columns:
    repeat(3, 1fr);

  gap:
    24px;
}


.loading-container {
  min-height:
    300px;

  display:
    flex;

  flex-direction:
    column;

  align-items:
    center;

  justify-content:
    center;
}


.loading-container .spinner-border {
  color:
    var(--primary);
}


.loading-container p {
  color:
    var(--text-secondary);

  margin-top:
    15px;
}


/* ========================================
   CTA
======================================== */

.cta-section {
  padding:
    70px 0 90px;
}


.cta-card {
  position:
    relative;

  min-height:
    330px;

  padding:
    55px;

  display:
    flex;

  align-items:
    center;

  border-radius:
    28px;

  overflow:
    hidden;

  background:
    linear-gradient(
      120deg,
      #321450,
      #5d267f,
      #8e4db2
    );

  box-shadow:
    0 20px 55px
    rgba(75,32,120,0.2);
}


.cta-content {
  position:
    relative;

  z-index:
    2;

  max-width:
    620px;
}


.cta-content .section-label {
  color:
    #dfcaff;
}


.cta-content h2 {
  color:
    #ffffff;

  font-size:
    clamp(
      2rem,
      4vw,
      3.2rem
    );

  line-height:
    1.1;

  font-weight:
    800;

  margin:
    8px 0 14px;
}


.cta-content p {
  color:
    rgba(255,255,255,0.78);

  line-height:
    1.7;

  max-width:
    530px;

  margin-bottom:
    25px;
}


.cta-button {
  display:
    inline-flex;

  align-items:
    center;

  gap:
    10px;

  padding:
    13px 20px;

  border-radius:
    10px;

  background:
    #ffffff;

  color:
    #4b2078;

  text-decoration:
    none;

  font-weight:
    700;
}


.cta-button:hover {
  color:
    #351252;

  transform:
    translateY(-3px);
}


/* ========================================
   DARK MODE
======================================== */

/*
   Important:
   These rules intentionally use !important
   because the project has global dark-mode
   rules that can override scoped component CSS.
*/


:global(body.dark-theme) .buyer-home {
  background:
    #0f0b18 !important;

  color:
    #f5f1ff !important;
}


/* Welcome */

:global(body.dark-theme) .welcome-card {
  background:
    #181225 !important;

  border-color:
    #302441 !important;
}


:global(body.dark-theme) .welcome-card h2 {
  color:
    #f5f1ff !important;

  opacity:
    1 !important;
}


:global(body.dark-theme) .welcome-card p {
  color:
    #b8aec5 !important;

  opacity:
    1 !important;
}


/* Section headings */

:global(body.dark-theme) .section-heading h2 {
  color:
    #f5f1ff !important;

  opacity:
    1 !important;
}


:global(body.dark-theme) .section-heading p {
  color:
    #b8aec5 !important;

  opacity:
    1 !important;
}


/* ========================================
   CATEGORY DARK MODE
======================================== */

:global(body.dark-theme) .category-card {
  background: #181225 !important;
  border-color: #342846 !important;
  color: #f7f3ff !important;
}

/* Make the category text visible */

:global(body.dark-theme) .category-card .category-text {
  color: #f7f3ff !important;
}

:global(body.dark-theme) .category-card .category-text h3 {
  color: #ffffff !important;
  background: transparent !important;
  opacity: 1 !important;
  visibility: visible !important;
  display: block !important;
}

:global(body.dark-theme) .category-card .category-text p {
  color: #c5bacf !important;
  background: transparent !important;
  opacity: 1 !important;
  visibility: visible !important;
  display: block !important;
}

:global(body.dark-theme) .category-card .category-arrow {
  color: #cdb9ff !important;
  background: transparent !important;
  opacity: 1 !important;
  visibility: visible !important;
  display: block !important;
}

:global(body.dark-theme) .category-card .category-icon {
  background: rgba(139, 92, 246, 0.16) !important;
  color: #cdb9ff !important;
  opacity: 1 !important;
  visibility: visible !important;
}

/* Remove Bootstrap/link color interference */

:global(body.dark-theme) .category-card,
:global(body.dark-theme) .category-card:hover,
:global(body.dark-theme) .category-card:focus,
:global(body.dark-theme) .category-card:active,
:global(body.dark-theme) .category-card:visited {
  color: #f7f3ff !important;
  text-decoration: none !important;
}

:global(body.dark-theme) .category-card:hover {
  background: #211931 !important;
  border-color: #5b3b78 !important;
}
/* ========================================
   RESPONSIVE
======================================== */

@media (max-width: 1100px) {

  .hero-decoration {
    opacity:
      0.55;
  }

  .category-grid {
    grid-template-columns:
      repeat(2, 1fr);
  }

  .featured-grid {
    grid-template-columns:
      repeat(2, 1fr);
  }

}


@media (max-width: 768px) {

  .buyer-hero,
  .hero-container {
    min-height:
      600px;
  }

  .buyer-content {
    padding:
      55px 0;
  }

  .buyer-content h1 {
    font-size:
      3.4rem;
  }

  .hero-decoration {
    opacity:
      0.18;

    right:
      -120px;

    width:
      80%;
  }

  .section-heading {
    align-items:
      flex-start;

    flex-direction:
      column;
  }

  .featured-grid {
    grid-template-columns:
      1fr;
  }

  .cta-card {
    padding:
      40px 28px;
  }

}


@media (max-width: 500px) {

  .buyer-content h1 {
    font-size:
      2.8rem;
  }

  .hero-buttons {
    flex-direction:
      column;
  }

  .hero-btn {
    width:
      100%;
  }

  .category-grid {
    grid-template-columns:
      1fr;
  }

  .welcome-card {
    flex-direction:
      column;

    align-items:
      flex-start;
  }

  .section-heading h2 {
    font-size:
      1.65rem;
  }

}

</style>